import React, { useRef, useEffect, useState } from 'react';

const AboutSection = () => {
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  const videoUrl = 'https://customer-assets.emergentagent.com/job_foxpress-creative/artifacts/0pm2llmc_Video.mp4';

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || prefersReducedMotion || isHovered || isInteracting) return;

    let animationId;
    let scrollSpeed = 0.5; // pixels per frame
    
    const autoScroll = () => {
      if (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight) {
        // Reset to top when reaching bottom
        scrollContainer.scrollTop = 0;
      } else {
        scrollContainer.scrollTop += scrollSpeed;
      }
      animationId = requestAnimationFrame(autoScroll);
    };

    // Start auto-scroll after a short delay
    const timeoutId = setTimeout(() => {
      animationId = requestAnimationFrame(autoScroll);
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [prefersReducedMotion, isHovered, isInteracting]);

  const handleInteractionStart = () => {
    setIsInteracting(true);
  };

  const handleInteractionEnd = () => {
    // Resume auto-scroll after a delay when user stops interacting
    setTimeout(() => setIsInteracting(false), 3000);
  };

  return (
    <section className="py-20 px-4 bg-[#040c14] relative overflow-hidden min-h-[80vh]">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        {/* Darker overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#040c14]/90"></div>
        {/* Vignette effect */}
        <div className="absolute inset-0 bg-radial-gradient"></div>
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 text-center drop-shadow-2xl" style={{ textShadow: '0 4px 20px rgba(0, 0, 0, 0.9), 0 2px 8px rgba(0, 0, 0, 0.8)' }}>
          About <span className="text-[#FF6500]">Augmented Cinematics</span>
        </h2>
        
        {/* Scrollable text container with enhanced visibility */}
        <div 
          ref={scrollRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onScroll={handleInteractionStart}
          onTouchStart={handleInteractionStart}
          onTouchEnd={handleInteractionEnd}
          onWheel={handleInteractionStart}
          className="
            h-[300px] md:h-[350px] lg:h-[400px]
            overflow-y-auto
            bg-black/60
            border border-[#FF6500]/30
            rounded-lg
            p-6 md:p-8
            scroll-smooth
            backdrop-blur-md
          "
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#FF6500 #152840'
          }}
        >
          <div className="space-y-6 text-white/90 text-base md:text-lg leading-relaxed">
            <p style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
              Augmented Cinematics is an independent creative studio producing motion-based cinematic visuals through a human-led, generative production workflow.
            </p>
            
            <p style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
              We use generative visual software as part of the creative process to support early-stage development, visualization, and promotional content. This allows stories, concepts, and brands to take visual form without the overhead of traditional film or animation pipelines. All creative direction, narrative decisions, and final outputs remain guided by human creatives.
            </p>
            
            <p style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
              In addition to working directly with authors, businesses, and content creators, Augmented Cinematics also operates as a white-label production partner for studios and creative teams during the developmental phase of film, series, and media projects. This work often involves exploratory visuals, proof-of-concept material, and generative cinematic effects created to support internal development, pitching, or early creative alignment.
            </p>
            
            <p style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
              Because much of this studio work is produced under white-label agreements, many projects are not publicly credited or released. Our focus remains on contributing effective visual material at the stages where speed, flexibility, and iteration matter most, rather than on public attribution.
            </p>
            
            <p style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}>
              Across all collaborations, our goal is consistent: to create purposeful cinematic assets that clarify ideas, support visibility, and fit how the material is meant to be used, without overstating outcomes or replacing traditional production when final delivery is required.
            </p>
            
            {/* Spacer for smooth scroll loop */}
            <div className="h-4"></div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="flex justify-center mt-4">
          <div className="text-white/50 text-xs flex items-center gap-2">
            <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span>Scroll to read more</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
